import { CREATE_REQUEST_STATUS, RECORD_DATA_TYPE } from '@demandproai/shared';
import { EASY_MODE_UPLOAD_STATUS } from '../constants/easyModeDocuments';

// UNCLASSIFIED is the state the record is created in, so it means the classifier has not answered
// yet. OTHER is an answer - the file could not be placed - and counts as classified.
export const getIsRecordPending = (record) =>
  !record?.recordType || record.recordType === RECORD_DATA_TYPE.UNCLASSIFIED;

// A record does not always end up under the key the browser uploaded to. The PFQ converts a doc or
// docx to PDF on ingest, writes the record under the new key and deletes the original, so a row that
// joined on its literal upload key would wait for a record that will never appear there. Only the
// extension changes and the basename is a uuid, so that is what the two sides join on.
export const getRecordJoinKey = (key = '') => {
  const dotIndex = key.lastIndexOf('.');

  return dotIndex > key.lastIndexOf('/') ? key.slice(0, dotIndex) : key;
};

export const buildRecordByJoinKey = ({ recordByS3UrlPath = {} }) =>
  Object.values(recordByS3UrlPath).reduce((accumulator, record) => {
    accumulator[getRecordJoinKey(record.s3UrlPath)] = record;

    return accumulator;
  }, {});

export const getRecordForUniqueKey = ({ recordByJoinKey = {}, uniqueKey = '' }) =>
  recordByJoinKey[getRecordJoinKey(uniqueKey)];

export const buildRecordByS3UrlPath = ({ records = [] }) =>
  records.reduce((accumulator, record) => {
    if (record?.s3UrlPath) {
      accumulator[record.s3UrlPath] = record;
    }

    return accumulator;
  }, {});

// Two polls can land out of order, so a fetched record may never rewind one we have already resolved.
export const mergeRecordByS3UrlPath = ({ previous = {}, records = [] }) => {
  const fetched = buildRecordByS3UrlPath({ records });

  return Object.entries(fetched).reduce(
    (accumulator, [s3UrlPath, record]) => {
      const existing = previous[s3UrlPath];
      const shouldKeepResolvedType = getIsRecordPending(record) && !getIsRecordPending(existing);

      accumulator[s3UrlPath] = shouldKeepResolvedType
        ? { ...record, recordType: existing.recordType, isPost: existing.isPost }
        : record;

      return accumulator;
    },
    { ...previous }
  );
};

// The pipeline gave up on the file. It never gets a recordType, so a row that only watched the type
// would sit at CLASSIFYING forever and never let the step settle.
export const getIsRecordUnreadable = (record) =>
  record?.status === CREATE_REQUEST_STATUS.BAD_RECORD;

// what the poll waits on: a record with no type yet, unless the pipeline has already given up on it
export const getIsRecordAwaitingClassification = (record) =>
  getIsRecordPending(record) && !getIsRecordUnreadable(record);

const getRowStatusFromRecord = (record) => {
  if (getIsRecordUnreadable(record)) return EASY_MODE_UPLOAD_STATUS.BAD_RECORD;

  return getIsRecordPending(record)
    ? EASY_MODE_UPLOAD_STATUS.CLASSIFYING
    : EASY_MODE_UPLOAD_STATUS.CLASSIFIED;
};

// Upload state wins while the bytes are still moving; classification only speaks for a row whose
// upload has already settled.
export const applyRecordToRow = ({ row, record }) => {
  if (!record || row.status !== EASY_MODE_UPLOAD_STATUS.UPLOADED) return row;

  return {
    ...row,
    status: getRowStatusFromRecord(record),
    recordType: record.recordType ?? row.recordType
  };
};

export const getFileNameFromS3UrlPath = (s3UrlPath = '') => s3UrlPath.split('/').pop() ?? s3UrlPath;
